"use client";

import { useMemo } from "react";
import {useLocale, useTranslations} from "next-intl";
import { getMonthObjects, monthFactory } from "@/factories/search-filters/search-filters-factory";
import { locationFilterFactory } from "@/factories/search-filters/location-filter-factory";
import useLocationSearch from "@/hooks/tour-search/use-location-search";
import { SearchFilters as Filters } from "@/types/dto/search-filters/search-filters";
import { SearchRange } from "@/types/dto/search-filters/range-filter";
import { filterOptionsByField, setFilterDisplayName, cleanFilterOptions } from "@/components/search-filters/search-filter-utils";
import { Locale } from "@/i18n/routing";

interface UseFilterProcessingProps {
  filters: Filters[];
  ranges: SearchRange[];
}

const useFilterProcessing = ({ filters, ranges }: UseFilterProcessingProps) => {
  const t = useTranslations("SEARCH");
  const locale = useLocale() as Locale;

  // Fetch all available locations for the filter dropdown (one-time call)
  const { data: locationData, isLoading: locationLoading } = useLocationSearch();

  // Process all filter data in one place
  const processedData = useMemo(() => {
    // Location filters - use API data if available, otherwise empty array
    const locationFilter = locationData?.Locations ?
      locationData.Locations.map(location => locationFilterFactory({
        locationType: location.locationType,
        locationName: location.locationName
      })) : [];

    // Main filters - use passed filters directly (already processed by API)
    const processedFilters = filters;

    // Use memoized mock data
    // const processMockSearchData = mockFiltersData;
    const processRanges = ranges || [];

    const dateRanges = processRanges.find((f) => f.fieldName === "dates_tdt");

    // Extract individual filters
    const badgesFilter = processedFilters.find((f) => f.fieldName === "Badges");
    const tourStyleFilter = processedFilters.find((f) => f.fieldName === "TourStyle");
    const activityLevelFilter = processedFilters.find((f) => f.fieldName === "ActivityLevel");
    const customerReviewFilter = processedFilters.find((f) => f.fieldName === "CustomerReview");
    const specialOffersFilter = processedFilters.find((f) => f.fieldName === "Offers");
    const durationFilter = processedFilters.find((f) => f.fieldName === "TourLengthDescription");
    const rangesFilter = processRanges.find((f) => f.fieldName === "price_tl");
    const smallGroupFilter = filterOptionsByField(tourStyleFilter, "displayName", t("Small Group"));
    const travelStylesCustomLabel = setFilterDisplayName(tourStyleFilter, t("Travel Styles"));
    const cleanDurationFilter = cleanFilterOptions(durationFilter);
    const durationCustomLabel = setFilterDisplayName(cleanDurationFilter, "Duration");
    // Date/month processing - handle undefined values gracefully with defaults
    const currentDate = new Date();

    const defaultMinValue = `${String(currentDate.getMonth() + 1).padStart(2, "0")}/${currentDate.getFullYear()}`;

    // Calculate 18 months from current month
    const futureDate = new Date(currentDate);
    futureDate.setMonth(currentDate.getMonth() + 23);
    const defaultMaxValue = `${String(futureDate.getMonth() + 1).padStart(2, "0")}/${futureDate.getFullYear()}`;

    // Ensure we always have valid string values before calling monthFactory

    let months: any[] = [];
    try {

      const minValue = (dateRanges?.minValue !== undefined) ? dateRanges.minValue : defaultMinValue;
      const maxValue = (dateRanges?.maxValue !== undefined) ? dateRanges.maxValue : defaultMaxValue;

      const currentMonth = monthFactory(defaultMinValue, locale);
      const minMonth = monthFactory(minValue, locale);
      const maxMonth = monthFactory(maxValue, locale);

      months = (currentMonth && minMonth && maxMonth) ? getMonthObjects(currentMonth, minMonth, maxMonth, locale) : [];
    } catch (error) {
      // If monthFactory fails, provide an empty array as fallback
      months = [];
    }


    return {
      locationFilter,
      badgesFilter,
      tourStyleFilter,
      activityLevelFilter,
      customerReviewFilter,
      specialOffersFilter,
      durationFilter,
      rangesFilter,
      smallGroupFilter,
      travelStylesCustomLabel,
      durationCustomLabel,
      months,
      locationLoading
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ranges, t, locale, locationData]);

  return processedData;
};
export default useFilterProcessing;